import {DOCUMENT} from '@angular/common';
import {computed, effect, inject, Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {TranslocoService} from '@jsverse/transloco';
import {filter, firstValueFrom} from 'rxjs';
import {
  APP_LANGUAGES,
  type AppLanguage,
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  normalizeLanguage,
} from './locale.config';

@Injectable({ providedIn: 'root' })
export class LanguageFacade {
  private readonly transloco = inject(TranslocoService);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  readonly availableLanguages = APP_LANGUAGES;
  readonly currentLanguage = computed(() =>
    normalizeLanguage(this.transloco.activeLang()),
  );

  /**
   * Creates an instance of the class and initializes a reactive effect.
   * The effect synchronizes the application's language settings by:
   * 1. Retrieving the current language.
   * 2. Updating the document's `lang` attribute to reflect the current language.
   * 3. Storing the current language in the browser's local storage.
   */
  constructor() {
    effect(() => {
      const language = this.currentLanguage();
      this.document.documentElement.lang = language;
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.applyUrlLanguageParameter());
  }

  /**
   * Initializes the translation service by setting the default language,
   * configuring fallback languages for missing translations, and loading
   * the initial active language.
   *
   * @return {Promise<void>} A promise that resolves when the initial language
   * loading is complete.
   */
  async initialize(): Promise<void> {
    this.transloco.setDefaultLang(DEFAULT_LANGUAGE);
    this.transloco.setFallbackLangForMissingTranslation({
      fallbackLang: FALLBACK_LANGUAGE,
    });

    const initialLanguage = this.resolveInitialLanguage();
    this.transloco.setActiveLang(initialLanguage);
    await firstValueFrom(this.transloco.load(initialLanguage));
  }

  /**
   * Sets the active language for the application.
   *
   * @param {AppLanguage} language - The language to be set as active.
   * @return {void} Does not return a value.
   */
  setLanguage(language: AppLanguage): void {
    this.transloco.setActiveLang(language);
  }

  /**
   * Resolves and returns the initial application language based on URL, user settings, or browser configuration.
   * It first checks for a `language` query parameter, then for a stored language preference.
   * If neither is present, it defaults to the browser's language settings.
   *
   * @return {AppLanguage} The resolved application language after normalization.
   */
  private resolveInitialLanguage(): AppLanguage {
    const urlLanguage = this.getLanguageParameterFromCurrentUrl();
    if (urlLanguage) {
      return normalizeLanguage(urlLanguage);
    }

    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLanguage) {
      return normalizeLanguage(storedLanguage);
    }

    return normalizeLanguage(window.navigator.language);
  }

  /**
   * Applies the current URL's `language` query parameter after router navigation.
   */
  private applyUrlLanguageParameter(): void {
    const urlLanguage = this.getLanguageParameterFromCurrentUrl();
    if (!urlLanguage) {
      return;
    }

    const language = normalizeLanguage(urlLanguage);
    if (language !== this.currentLanguage()) {
      this.setLanguage(language);
    }
  }

  /**
   * Reads the current URL's `language` query parameter.
   *
   * @return Language parameter, or `null` when it is missing.
   */
  private getLanguageParameterFromCurrentUrl(): string | null {
    return new URLSearchParams(window.location.search).get('language')?.trim() || null;
  }
}
